import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import guestApi from '../utils/guestApi';
import '../styles/GuestSavePage.css';
import {
  trackGuestContinuationLinkUsed,
  trackGuestVaultSaveLegacyVerified,
} from '../utils/guestFunnelAnalytics';

/**
 * Email continuation link: restores guest cookie on this device (save already exists server-side).
 * Legacy: old verify-first magic links still complete a first-time save here.
 */
const GuestSavePage = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('loading'); // loading | success | error
  const [message, setMessage] = useState('');
  const [savedName, setSavedName] = useState('');
  const [isContinuation, setIsContinuation] = useState(false);
  const [retryKey, setRetryKey] = useState(0);

  useEffect(() => {
    const token = searchParams.get('t');
    if (!token || token.length > 200) {
      trackGuestContinuationLinkUsed('invalid', 'missing_token');
      setStatus('error');
      setMessage('This link is invalid. Open their card again and choose “Save live contact” if you still need it.');
      return;
    }

    let cancelled = false;
    setStatus('loading');
    (async () => {
      try {
        const res = await guestApi.post('/api/guest/verify', { token });
        if (cancelled) return;
        if (res.data?.ok) {
          const cont = !!res.data.continuation;
          setIsContinuation(cont);
          trackGuestContinuationLinkUsed('success', cont ? 'continuation' : 'legacy_verify');
          if (!cont && res.data.nfcUID) {
            trackGuestVaultSaveLegacyVerified(res.data.nfcUID);
          }
          setStatus('success');
          setSavedName(res.data.savedName || 'Contact');
          setMessage(res.data.message || 'Your live card save is ready.');
        } else {
          trackGuestContinuationLinkUsed('error', 'not_ok');
          setStatus('error');
          setMessage('Something went wrong. Please try again from the card page.');
        }
      } catch (err) {
        if (cancelled) return;
        const code = err.response?.data?.error;
        if (code === 'invalid_or_expired') {
          trackGuestContinuationLinkUsed('expired');
        } else if (code === 'vault_expired') {
          trackGuestContinuationLinkUsed('expired', 'vault');
        } else if (code === 'invalid_token') {
          trackGuestContinuationLinkUsed('invalid', 'token');
        } else if (code === 'card_unavailable') {
          trackGuestContinuationLinkUsed('error', 'card_unavailable');
        } else {
          trackGuestContinuationLinkUsed('error', typeof code === 'string' ? code : 'request');
        }
        const msg =
          err.response?.data?.message ||
          err.response?.data?.error ||
          'This link is invalid or has expired. Open their card and use “Save live contact” if you still need it.';
        setStatus('error');
        setMessage(typeof msg === 'string' ? msg : 'Please try again from the card page.');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [searchParams, retryKey]);

  return (
    <div className="guest-save-page">
      <div className="guest-save-card">
        {status === 'loading' && (
          <>
            <h1 className="guest-save-title">Opening your link…</h1>
            <p className="guest-save-sub">One moment.</p>
          </>
        )}
        {status === 'success' && (
          <>
            <h1 className="guest-save-title">{isContinuation ? 'You’re connected' : 'Contact saved'}</h1>
            <p className="guest-save-sub">
              {isContinuation ? (
                <>
                  This browser can open your saved live card for <strong>{savedName}</strong>. Sign in with the
                  same email when you’re ready to keep it in your Velox account.
                </>
              ) : (
                <>
                  <strong>{savedName}</strong> is held for you as a live digital card. Sign in later with the same
                  email if you want to save to your account.
                </>
              )}
            </p>
            <Link to="/login" className="guest-save-button guest-save-button-primary">
              Sign in to your account
            </Link>
          </>
        )}
        {status === 'error' && (
          <>
            <h1 className="guest-save-title">Link unavailable</h1>
            <p className="guest-save-sub">{message}</p>
            {typeof navigator !== 'undefined' && !navigator.onLine && (
              <p className="guest-save-sub">Connect to the internet, then try again.</p>
            )}
            <button
              type="button"
              className="guest-save-button guest-save-button-primary"
              style={{ marginBottom: '0.75rem', width: '100%', cursor: 'pointer' }}
              onClick={() => setRetryKey((k) => k + 1)}
              disabled={typeof navigator !== 'undefined' && !navigator.onLine}
            >
              Try again
            </button>
            <Link to="/" className="guest-save-button guest-save-button-secondary" style={{ width: '100%' }}>
              Go home
            </Link>
          </>
        )}
      </div>
    </div>
  );
};

export default GuestSavePage;
